import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/** @extends {Component<{option:HTMLSelectElement,quantity:HTMLInputElement,add:HTMLButtonElement,price:HTMLElement,status:HTMLElement}>} */
class MedorraBuyOnline extends Component {
  requiredRefs = ['option', 'quantity', 'add', 'price', 'status'];

  connectedCallback() {
    super.connectedCallback();
    this.updateSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateSelection();
  }

  updateSelection() {
    const { option, quantity, add, price, status } = this.refs;
    const selected = option.selectedOptions[0];
    const priced = Number(selected?.dataset.price) > 0;
    const available = selected?.dataset.available === 'true' && priced && !selected.disabled;
    add.disabled = !available;
    add.textContent = !priced ? this.dataset.quote : available ? this.dataset.add : this.dataset.soldOut;
    price.textContent = priced ? selected.dataset.priceLabel : this.dataset.quote;
    status.textContent = available ? this.dataset.ready : this.dataset.unavailable;
    quantity.min = selected?.dataset.min || '1';
    quantity.step = selected?.dataset.step || '1';
    if (selected?.dataset.max) quantity.max = selected.dataset.max;
    else quantity.removeAttribute('max');
    if (!quantity.validity.valid) quantity.value = quantity.min;
    this.dispatchEvent(new CustomEvent('medorra:buy-selection', { bubbles: true, detail: { id: option.value } }));
  }

  /** @param {MouseEvent} event */
  checkPurchase(event) {
    const selected = this.refs.option.selectedOptions[0];
    if (!selected || selected.disabled || selected.dataset.available !== 'true' || Number(selected.dataset.price) <= 0 || !this.refs.quantity.reportValidity()) {
      event.preventDefault();
    }
  }
}

if (!customElements.get('medorra-buy-online')) customElements.define('medorra-buy-online', MedorraBuyOnline);

class MedorraBuyingHelp extends HTMLElement {
  #controller;
  #key = 'medorra-supply-list-v1';

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#click, options);
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#productSelected, options);
    section?.addEventListener('change', this.#legacySelected, options);
    section?.addEventListener('medorra:buy-selection', event => this.#showVariant(event.detail.id), options);
    const legacyOption = section?.querySelector('medorra-buy-online select[name="id"]');
    if (legacyOption) this.#showVariant(legacyOption.value);
    window.addEventListener('storage', this.#renderList, options);
    window.addEventListener('medorra:supply-list', this.#renderList, options);
    this.#renderList();
  }

  disconnectedCallback() { this.#controller?.abort(); }

  #productSelected = async event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    try {
      const { detail } = await event.promise;
      if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
      const replacement = detail?.html?.querySelector(`medorra-buying-help[data-product-id="${this.dataset.productId}"]`);
      const current = this.querySelector('[data-variant-information]');
      const next = replacement?.querySelector('[data-variant-information]');
      if (current && next) current.replaceWith(next.cloneNode(true));
      else if (detail?.resource?.id) this.#showVariant(detail.resource.id);
    } catch { }
  };

  #legacySelected = event => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('medorra-buy-online select[name="id"]')) this.#showVariant(event.target.value);
  };

  #showVariant(id) {
    for (const panel of this.querySelectorAll('[data-buying-variant]')) panel.hidden = panel.dataset.buyingVariant !== String(id).split('/').pop();
  }

  #items() {
    try {
      const items = JSON.parse(localStorage.getItem(this.#key) || '[]');
      if (!Array.isArray(items)) return [];
      return items.filter(item => {
        if (!item || typeof item.name !== 'string' || typeof item.url !== 'string' || !/^\d+$/.test(item.id)) return false;
        const url = new URL(item.url, location.origin);
        return url.origin === location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\//i.test(url.pathname);
      }).slice(0, 50);
    } catch { return []; }
  }

  #click = event => {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    const status = this.querySelector('[data-supply-status]');
    let items = this.#items();
    if (button.hasAttribute('data-save-supply')) {
      if (!items.some(item => item.id === button.dataset.variant)) {
        if (items.length >= 50) { status.textContent = this.dataset.full; return; }
        items.push({ id: button.dataset.variant, name: button.dataset.name, url: button.dataset.url });
      }
    } else if (button.dataset.removeSupply) {
      items = items.filter(item => item.id !== button.dataset.removeSupply);
    } else return;
    try {
      localStorage.setItem(this.#key, JSON.stringify(items));
      window.dispatchEvent(new Event('medorra:supply-list'));
      status.textContent = button.hasAttribute('data-save-supply') ? this.dataset.saved : '';
    } catch { status.textContent = this.dataset.error; }
  };

  #renderList = () => {
    const list = this.querySelector('[data-supply-list]');
    if (!list) return;
    const items = this.#items();
    // A catalog-only list stays out of the way until the customer saves an item.
    if (!this.dataset.productId) this.hidden = items.length === 0;
    this.querySelector('[data-supply-count]').textContent = items.length ? `(${items.length})` : '';
    list.replaceChildren();
    if (!items.length) {
      const li = document.createElement('li'); li.textContent = this.dataset.empty; list.append(li);
    }
    for (const item of items) {
      const li = document.createElement('li');
      const link = document.createElement('a'); link.href = item.url; link.textContent = item.name;
      const button = document.createElement('button'); button.type = 'button'; button.dataset.removeSupply = item.id; button.textContent = this.dataset.remove; button.setAttribute('aria-label', `${this.dataset.remove}: ${item.name}`);
      li.append(link, button); list.append(li);
    }
  };
}
if (!customElements.get('medorra-buying-help')) customElements.define('medorra-buying-help', MedorraBuyingHelp);
